import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Card, Form, Button, Row, Col, 
  Spinner, Alert 
} from 'react-bootstrap';
import { FiSave, FiArrowLeft, FiAlertTriangle } from 'react-icons/fi';
import casesService, { Case } from '../../services/cases.service';
import portfoliosService, { Portfolio } from '../../services/portfolios.service';
import api from '../../services/api';

interface ProcessType {
  id: number;
  name: string;
  code: string;
}

interface User {
  id: number;
  full_name: string;
  email: string;
  is_active?: boolean;
}

interface Court {
  id: number;
  name: string;
  jurisdiction?: string;
  city?: string;
  department?: string;
}

const CaseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [processTypes, setProcessTypes] = useState<ProcessType[]>([]);
  const [lawyers, setLawyers] = useState<User[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);

  const [formData, setFormData] = useState({
    has_case_number: 'SI', // "SI" | "NO"
    case_number: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    portfolio_id: '',
    process_type_id: '',
    plaintiff: '',
    defendant: '',
    court_id: '',
    court_name: '',
    jurisdiction_entity: '',
    expediente_url: '',
    status: 'active',
    assigned_lawyer_id: '',
    filing_date: '',
    registration_date: '',
    last_action_notes: '',
    next_action_date: '',
    next_action_description: '',
    next_hearing_date: '',
    next_hearing_time: '',
    hearing_rescheduled: false,
    rescheduled_hearing_date: '',
    process_status: '',
    // FNG / FAG
    has_fng_fag: 'NO' as 'SI' | 'NO',
    fng_fag_type: '' as '' | 'FNG' | 'FAG' | 'AMBOS',
    fng_fag_docs_sent: '' as '' | 'SI' | 'NO',
    fng_fag_docs_sent_at: '',
    // Facturación
    billing_criteria: '',
    billing_trigger: '' as '' | 'AL_PRESENTAR' | 'AL_NOTIFICAR' | 'AL_OBTENER_SENTENCIA' | 'MENSUAL' | 'POR_ETAPA' | 'OTRO'
  });

  useEffect(() => {
    loadProcessTypes();
    loadLawyers();
    loadCourts();
    loadPortfolios();
    if (isEditing) {
      loadCase();
    }
  }, [id]);

  const loadProcessTypes = async () => {
    try {
      const response = await api.get('/admin/process-types');
      setProcessTypes(response.data.data || []);
    } catch (error) {
      console.error('Error loading process types:', error);
    }
  };

  const loadLawyers = async () => {
    try {
      const response = await api.get('/admin/users');
      const users = response.data.data || response.data || [];
      setLawyers(users.filter((u: any) => u.is_active !== false));
    } catch (error) {
      console.error('Error loading lawyers:', error);
    }
  };

  const loadCourts = async () => {
    try {
      const response = await api.get('/admin/courts');
      setCourts(response.data.data || []);
    } catch (error) {
      console.error('Error loading courts:', error);
    }
  };

  const loadPortfolios = async () => {
    try {
      const list = await portfoliosService.list();
      setPortfolios(list);
    } catch (error) {
      console.error('Error loading portfolios:', error);
    }
  };

  const loadCase = async () => {
    try {
      setLoading(true);
      const caso: Case = await casesService.getById(Number(id));
      setFormData({
        has_case_number: caso.has_case_number === false ? 'NO' : 'SI',
        case_number: (caso.case_number && !caso.case_number.startsWith('PENDIENTE')) ? caso.case_number : '',
        client_name: caso.client_name || '',
        client_email: caso.client_email || '',
        client_phone: caso.client_phone || '',
        portfolio_id: (caso as any).portfolio_id?.toString() || '',
        process_type_id: caso.process_type_id?.toString() || '',
        plaintiff: caso.plaintiff || '',
        defendant: caso.defendant || '',
        court_id: caso.court_id?.toString() || '',
        court_name: caso.court_name || '',
        jurisdiction_entity: caso.jurisdiction_entity || '',
        expediente_url: caso.expediente_url || '',
        status: (caso.status || 'active') as any,
        assigned_lawyer_id: caso.assigned_lawyer_id?.toString() || '',
        filing_date: caso.filing_date
          ? new Date(caso.filing_date).toISOString().split('T')[0]
          : '',
        registration_date: caso.registration_date
          ? new Date(caso.registration_date).toISOString().split('T')[0]
          : '',
        last_action_notes: caso.last_action_notes || '',
        next_action_date: caso.next_action_date 
          ? new Date(caso.next_action_date).toISOString().split('T')[0] 
          : '',
        next_action_description: caso.next_action_description || '',
        next_hearing_date: caso.next_hearing_date
          ? new Date(caso.next_hearing_date).toISOString().split('T')[0]
          : '',
        next_hearing_time: caso.next_hearing_time || '',
        hearing_rescheduled: caso.hearing_rescheduled || false,
        rescheduled_hearing_date: caso.rescheduled_hearing_date
          ? new Date(caso.rescheduled_hearing_date).toISOString().split('T')[0]
          : '',
        process_status: caso.process_status || '',
        has_fng_fag: caso.has_fng_fag ? 'SI' : 'NO',
        fng_fag_type: (caso.fng_fag_type || '') as any,
        fng_fag_docs_sent: caso.fng_fag_docs_sent === null || caso.fng_fag_docs_sent === undefined
          ? ''
          : (caso.fng_fag_docs_sent ? 'SI' : 'NO'),
        fng_fag_docs_sent_at: caso.fng_fag_docs_sent_at
          ? new Date(caso.fng_fag_docs_sent_at).toISOString().split('T')[0]
          : '',
        billing_criteria: caso.billing_criteria || '',
        billing_trigger: (caso.billing_trigger || '') as any
      });
    } catch (error) {
      setError('Error al cargar el caso');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => {
        const next: any = { ...prev, [name]: value };
        if (name === 'court_id' && value) {
          const c = courts.find(x => String(x.id) === String(value));
          if (c) next.court_name = c.name;
        }
        if (name === 'has_case_number' && value === 'NO') {
          next.case_number = '';
        }
        if (name === 'has_fng_fag' && value === 'NO') {
          next.fng_fag_type = '';
          next.fng_fag_docs_sent = '';
          next.fng_fag_docs_sent_at = '';
        }
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const hasCaseNumber = formData.has_case_number === 'SI';
    if (hasCaseNumber && !formData.case_number.trim()) {
      setError('El número de radicado es obligatorio cuando "¿Tiene radicado?" está en SI');
      return;
    }
    if (formData.has_fng_fag === 'SI') {
      if (!formData.fng_fag_type) {
        setError('Debe indicar si es FNG, FAG o AMBOS');
        return;
      }
      if (!formData.fng_fag_docs_sent) {
        setError('Debe indicar si se remitieron los documentos para cobro FNG/FAG (SI/NO)');
        return;
      }
    }

    setSaving(true);
    try {
      const dataToSend: any = {
        has_case_number: hasCaseNumber,
        case_number: hasCaseNumber ? formData.case_number.trim() : null,
        client_name: formData.client_name,
        client_email: formData.client_email || null,
        client_phone: formData.client_phone || null,
        process_type_id: formData.process_type_id ? Number(formData.process_type_id) : null,
        plaintiff: formData.plaintiff || null,
        defendant: formData.defendant || null,
        court_id: formData.court_id ? Number(formData.court_id) : null,
        court_name: formData.court_name || null,
        jurisdiction_entity: formData.jurisdiction_entity || null,
        expediente_url: formData.expediente_url || null,
        status: formData.status,
        assigned_lawyer_id: formData.assigned_lawyer_id ? Number(formData.assigned_lawyer_id) : null,
        filing_date: formData.filing_date || null,
        registration_date: formData.registration_date || null,
        last_action_notes: formData.last_action_notes || null,
        next_action_date: formData.next_action_date || null,
        next_action_description: formData.next_action_description || null,
        next_hearing_date: formData.next_hearing_date || null,
        next_hearing_time: formData.next_hearing_time || null,
        hearing_rescheduled: !!formData.hearing_rescheduled,
        rescheduled_hearing_date: formData.rescheduled_hearing_date || null,
        process_status: formData.process_status || null,
        has_fng_fag: formData.has_fng_fag === 'SI',
        fng_fag_type: formData.has_fng_fag === 'SI' ? formData.fng_fag_type || null : null,
        fng_fag_docs_sent:
          formData.has_fng_fag === 'SI'
            ? formData.fng_fag_docs_sent === 'SI'
              ? true
              : formData.fng_fag_docs_sent === 'NO'
              ? false
              : null
            : null,
        fng_fag_docs_sent_at: formData.fng_fag_docs_sent_at || null,
        billing_criteria: formData.billing_criteria || null,
        billing_trigger: formData.billing_trigger || null,
        portfolio_id: formData.portfolio_id ? Number(formData.portfolio_id) : null
      };

      if (isEditing) {
        await casesService.update(Number(id), dataToSend);
      } else {
        await casesService.create(dataToSend);
      }
      
      navigate('/cases');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error al guardar el caso');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const groupedCourts = courts.reduce<Record<string, Court[]>>((acc, c) => {
    const key = c.jurisdiction || 'OTROS';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  return (
    <div className="case-form">
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="page-title mb-1">
              {isEditing ? 'Editar Caso' : 'Nuevo Caso'}
            </h4>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
                <li className="breadcrumb-item"><Link to="/cases">Casos</Link></li>
                <li className="breadcrumb-item active">
                  {isEditing ? 'Editar' : 'Nuevo'}
                </li>
              </ol>
            </nav>
          </div>
          <Button 
            variant="light" 
            onClick={() => navigate('/cases')}
          >
            <FiArrowLeft className="me-2" />
            Volver
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Información del Caso</h5>
              </Card.Header>
              <Card.Body>
                {/* Radicado con selector SI/NO (REVISION) */}
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>¿Tiene radicado? *</Form.Label>
                      <Form.Select
                        name="has_case_number"
                        value={formData.has_case_number}
                        onChange={handleChange}
                        required
                      >
                        <option value="SI">SI</option>
                        <option value="NO">NO (queda pendiente)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Radicado {formData.has_case_number === 'SI' && '*'}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="case_number"
                        value={formData.case_number}
                        onChange={handleChange}
                        required={formData.has_case_number === 'SI'}
                        disabled={formData.has_case_number === 'NO'}
                        placeholder={
                          formData.has_case_number === 'NO'
                            ? 'PENDIENTE'
                            : 'Ej: 11001310302520240016700'
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tipo de Proceso</Form.Label>
                      <Form.Select
                        name="process_type_id"
                        value={formData.process_type_id}
                        onChange={handleChange}
                      >
                        <option value="">Seleccionar...</option>
                        {processTypes.map(pt => (
                          <option key={pt.id} value={pt.id}>{pt.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cartera / Portafolio</Form.Label>
                      <Form.Select
                        name="portfolio_id"
                        value={formData.portfolio_id}
                        onChange={handleChange}
                      >
                        <option value="">Sin cartera</option>
                        {portfolios.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Segmenta el caso por cliente/banco para reportes por cartera.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Link del Expediente</Form.Label>
                  <Form.Control
                    type="url"
                    name="expediente_url"
                    value={formData.expediente_url}
                    onChange={handleChange}
                    placeholder="https://consultaprocesos.ramajudicial.gov.co/..."
                  />
                  <Form.Text className="text-muted">
                    URL para consultar el expediente en línea
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Demandante</Form.Label>
                      <Form.Control
                        type="text"
                        name="plaintiff"
                        value={formData.plaintiff}
                        onChange={handleChange}
                        placeholder="Nombre del demandante"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Demandado</Form.Label>
                      <Form.Control
                        type="text"
                        name="defendant"
                        value={formData.defendant}
                        onChange={handleChange}
                        placeholder="Nombre del demandado"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Juzgado como dropdown (REVISION) */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Juzgado</Form.Label>
                      <Form.Select
                        name="court_id"
                        value={formData.court_id}
                        onChange={handleChange}
                      >
                        <option value="">Seleccionar juzgado...</option>
                        {Object.keys(groupedCourts).sort().map(jur => (
                          <optgroup key={jur} label={jur}>
                            {groupedCourts[jur].map(c => (
                              <option key={c.id} value={c.id}>
                                {c.name}{c.city ? ` – ${c.city}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Lista enviada por Jurídica. Si no aparece, agrégalo en Administración.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Entidad / Jurisdicción</Form.Label>
                      <Form.Control
                        type="text"
                        name="jurisdiction_entity"
                        value={formData.jurisdiction_entity}
                        onChange={handleChange}
                        placeholder="Jurisdicción / Entidad"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Abogado Encargado</Form.Label>
                      <Form.Select
                        name="assigned_lawyer_id"
                        value={formData.assigned_lawyer_id}
                        onChange={handleChange}
                      >
                        <option value="">Seleccionar...</option>
                        {lawyers.map(lawyer => (
                          <option key={lawyer.id} value={lawyer.id}>{lawyer.full_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Estado del Proceso</Form.Label>
                      <Form.Select
                        name="process_status"
                        value={formData.process_status}
                        onChange={handleChange}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="PRESENTACION">Presentación</option>
                        <option value="MANDAMIENTO DE PAGO">Mandamiento de Pago</option>
                        <option value="PARA DILIGENCIA">Para Diligencia</option>
                        <option value="PARA AUDIENCIA">Para Audiencia</option>
                        <option value="TRASLADO DE CONTESTACIÓN">Traslado de Contestación</option>
                        <option value="RECURSO DE REPOSICION">Recurso de Reposición</option>
                        <option value="LIQUIDACION">Liquidación</option>
                        <option value="PARA SENTENCIA">Para Sentencia</option>
                        <option value="DESISTIMIENTO TACITO">Desistimiento Tácito</option>
                        <option value="TERMINADO">Terminado</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fecha de Radicación</Form.Label>
                      <Form.Control
                        type="date"
                        name="filing_date"
                        value={formData.filing_date}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fecha de Registro</Form.Label>
                      <Form.Control
                        type="date"
                        name="registration_date"
                        value={formData.registration_date}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Estado General</Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="active">Activo</option>
                        <option value="suspended">Suspendido</option>
                        <option value="closed">Cerrado</option>
                        <option value="archived">Archivado</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Audiencias y Seguimiento */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Audiencias y Seguimiento</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fecha Audiencia</Form.Label>
                      <Form.Control
                        type="date"
                        name="next_hearing_date"
                        value={formData.next_hearing_date}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Hora</Form.Label>
                      <Form.Control
                        type="time"
                        name="next_hearing_time"
                        value={formData.next_hearing_time}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="hearing_rescheduled"
                        label="¿Se fijó nueva fecha de audiencia?"
                        checked={formData.hearing_rescheduled}
                        onChange={handleChange}
                        style={{ marginTop: '2rem' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {formData.hearing_rescheduled && (
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nueva Fecha de Audiencia</Form.Label>
                        <Form.Control
                          type="date"
                          name="rescheduled_hearing_date"
                          value={formData.rescheduled_hearing_date}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Observación General - Última Actuación</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="last_action_notes"
                    value={formData.last_action_notes}
                    onChange={handleChange}
                    placeholder="Notas sobre la última actuación del proceso..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Próxima Actuación */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Próxima Actuación</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fecha</Form.Label>
                      <Form.Control
                        type="date"
                        name="next_action_date"
                        value={formData.next_action_date}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group>
                  <Form.Label>Descripción de la Actuación</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="next_action_description"
                    value={formData.next_action_description}
                    onChange={handleChange}
                    placeholder="Descripción de la próxima actuación..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            {/* FNG / FAG (REVISION) */}
            <Card className="mb-4 border-warning">
              <Card.Header className="bg-warning-subtle">
                <h5 className="mb-0">
                  <FiAlertTriangle className="me-2" />
                  Garantías FNG / FAG (Serfinanza, Banco Agrario, etc.)
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>¿Tiene FNG o FAG? *</Form.Label>
                      <Form.Select
                        name="has_fng_fag"
                        value={formData.has_fng_fag}
                        onChange={handleChange}
                      >
                        <option value="NO">NO</option>
                        <option value="SI">SI</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {formData.has_fng_fag === 'SI' && (
                    <>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Tipo de garantía *</Form.Label>
                          <Form.Select
                            name="fng_fag_type"
                            value={formData.fng_fag_type}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Seleccionar...</option>
                            <option value="FNG">FNG</option>
                            <option value="FAG">FAG</option>
                            <option value="AMBOS">AMBOS</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Remisión de documentos para cobro FNG/FAG *</Form.Label>
                          <Form.Select
                            name="fng_fag_docs_sent"
                            value={formData.fng_fag_docs_sent}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Seleccionar...</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </Form.Select>
                          {formData.fng_fag_docs_sent === 'NO' && (
                            <Form.Text className="text-danger">
                              Se generará alerta por correo recordando que se deben enviar.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </>
                  )}
                </Row>
                {formData.has_fng_fag === 'SI' && formData.fng_fag_docs_sent === 'SI' && (
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Fecha de remisión</Form.Label>
                        <Form.Control
                          type="date"
                          name="fng_fag_docs_sent_at"
                          value={formData.fng_fag_docs_sent_at}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            {/* Facturación (REVISION) */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Facturación</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Disparador de Facturación</Form.Label>
                      <Form.Select
                        name="billing_trigger"
                        value={formData.billing_trigger}
                        onChange={handleChange}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="AL_PRESENTAR">Al presentar la demanda</option>
                        <option value="AL_NOTIFICAR">Al notificar al demandado</option>
                        <option value="AL_OBTENER_SENTENCIA">Al obtener sentencia (ej. Serfinanza)</option>
                        <option value="MENSUAL">Mensual</option>
                        <option value="POR_ETAPA">Por etapa procesal</option>
                        <option value="OTRO">Otro (ver criterio)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Criterio específico de facturación (según cliente)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="billing_criteria"
                    value={formData.billing_criteria}
                    onChange={handleChange}
                    placeholder="Ej: Serfinanza paga solo si hay sentencia. Banco Agrario paga por etapa..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* Información del Cliente */}
          <Col lg={4}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Información del Cliente</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleChange}
                    required
                    placeholder="Nombre completo"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Correo Electrónico</Form.Label>
                  <Form.Control
                    type="email"
                    name="client_email"
                    value={formData.client_email}
                    onChange={handleChange}
                    placeholder="cliente@email.com"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type="tel"
                    name="client_phone"
                    value={formData.client_phone}
                    onChange={handleChange}
                    placeholder="+57 300 123 4567"
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <div className="d-grid">
              <Button 
                type="submit" 
                variant="primary" 
                size="lg"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FiSave className="me-2" />
                    {isEditing ? 'Actualizar Caso' : 'Crear Caso'}
                  </>
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CaseForm;
